import { WifiOff, RefreshCw, Brain } from 'lucide-react';
import { Button } from './ui/button';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import { TranslatedText } from './translated-text';
import { useTranslation } from '../hooks/useTranslation';
import { useToast } from '../hooks/use-toast';
import { useState, useEffect } from 'react';

// Monsterado Lounge bar & nightlife colors
const BRAND_COLORS = {
  primary: '#b8516f', // Rose Pink
  secondary: '#742425', // Velvet Wine
  dark: '#0E0E12', // Midnight Obsidian
  darkGrey: '#1C1C24', // Rich dark grey
  accent: '#f8d4de', // Rose Blush Accent
};

interface OfflineScreenProps {
  showFullScreen?: boolean;
  onRetry?: () => void;
}

/**
 * Full-screen branded offline component
 * Takes over the entire app when offline with Monsterado Lounge branding
 */
export function OfflineScreen({ 
  showFullScreen = true,
  onRetry 
}: OfflineScreenProps) {
  const { isOnline } = useNetworkStatus();
  const { tSync } = useTranslation();
  const { toast } = useToast();
  const [isRetrying, setIsRetrying] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  const handleRetry = async () => {
    setIsRetrying(true);
    setRetryCount((prev) => prev + 1);
    
    try {
      const response = await fetch('/api/health', {
        method: 'HEAD',
        cache: 'no-cache',
        signal: AbortSignal.timeout(5000),
      });
      
      if (response.ok) {
        toast({
          title: tSync('Connection Restored'),
          description: tSync('You are back online'),
        });
        if (onRetry) {
          onRetry();
        }
      } else {
        throw new Error('Connection failed');
      }
    } catch (error) {
      toast({
        title: tSync('Still Offline'),
        description: tSync('Unable to connect. Please check your internet connection and try again.'),
        variant: 'destructive',
      });
    } finally {
      setIsRetrying(false);
    }
  };

  // Auto-retry when connection is restored
  useEffect(() => {
    if (isOnline && retryCount > 0) {
      toast({
        title: tSync('Connection Restored'),
        description: tSync('You are back online'),
      });
      if (onRetry) {
        onRetry();
      }
    }
  }, [isOnline, retryCount, onRetry, toast, tSync]);

  if (isOnline) {
    return null;
  }

  // Full-screen overlay with branded design - simplified and compact
  return (
    <div 
      className="fixed top-0 left-0 right-0 bottom-0 z-[99999] flex items-center justify-center p-4 overflow-auto"
      style={{ 
        background: `linear-gradient(135deg, ${BRAND_COLORS.accent}20 0%, ${BRAND_COLORS.secondary}15 50%, ${BRAND_COLORS.primary}20 100%)`
      }}
    >
      <div className="w-full max-w-lg mx-auto">
        {/* Branded Header with Logo */}
        <div className="text-center mb-4">
          <div className="flex items-center justify-center gap-3">
            <img 
              src="/brand-e.svg" 
              alt="Monsterado Lounge" 
              className="w-14 h-14 object-contain drop-shadow-[0_2px_12px_rgba(184,81,111,0.35)]" 
            />
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">
              Monsterado Lounge
            </h1>
          </div>
        </div>

        {/* Main Content Card - Simplified */}
        <div className="bg-slate-50 dark:bg-slate-900/95 dark:bg-slate-800/95 backdrop-blur-lg rounded-xl border border-slate-200 dark:border-slate-800/50 dark:border-slate-700/50 p-6 overflow-hidden">
          {/* Offline Icon and Message */}
          <div className="text-center mb-6">
            <div className="relative inline-flex mb-4">
              <div 
                className="w-20 h-20 rounded-full flex items-center justify-center"
                style={{ 
                  background: `linear-gradient(135deg, ${BRAND_COLORS.accent} 0%, ${BRAND_COLORS.secondary}80 100%)`
                }}
              >
                <WifiOff className="w-10 h-10" style={{ color: BRAND_COLORS.primary }} />
              </div>
              <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-50 dark:bg-red-950/200 rounded-full border-2 border-white dark:border-slate-800 animate-pulse"></div>
            </div>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-2">
              <TranslatedText text="No Internet Connection" />
            </h2>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              <TranslatedText text="Please check your connection and try again" />
            </p>
          </div>

          {/* Quick Tips - Simplified */}
          <div 
            className="rounded-lg p-4 mb-6"
            style={{ 
              backgroundColor: `${BRAND_COLORS.accent}40`,
              border: `1px solid ${BRAND_COLORS.primary}40`
            }}
          >
            <p className="text-xs font-medium mb-2" style={{ color: BRAND_COLORS.dark }}>
              <TranslatedText text="Quick tips:" />
            </p>
            <ul className="text-xs space-y-1" style={{ color: BRAND_COLORS.darkGrey }}>
              <li>• <TranslatedText text="Check Wi-Fi or mobile data" /></li>
              <li>• <TranslatedText text="Disable airplane mode" /></li>
              <li>• <TranslatedText text="Refresh the page" /></li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              onClick={handleRetry}
              disabled={isRetrying}
              className="flex-1 text-white"
              style={{ 
                background: `linear-gradient(135deg, ${BRAND_COLORS.primary} 0%, ${BRAND_COLORS.secondary} 100%)`,
                border: 'none'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = `linear-gradient(135deg, ${BRAND_COLORS.secondary} 0%, ${BRAND_COLORS.primary} 100%)`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = `linear-gradient(135deg, ${BRAND_COLORS.primary} 0%, ${BRAND_COLORS.secondary} 100%)`;
              }}
            >
              {isRetrying ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  <TranslatedText text="Checking..." />
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  <TranslatedText text="Try Again" />
                </>
              )}
            </Button>
            
            <Button
              variant="outline"
              onClick={() => window.location.reload()}
              style={{ 
                borderColor: BRAND_COLORS.primary,
                color: BRAND_COLORS.primary
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = `${BRAND_COLORS.accent}40`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <TranslatedText text="Refresh" />
            </Button>
          </div>

          {/* Network Status */}
          <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700 text-center">
            <div className="flex items-center justify-center gap-2 text-xs text-slate-500 dark:text-slate-400">
              <div className="w-2 h-2 bg-red-50 dark:bg-red-950/200 rounded-full animate-pulse"></div>
              <span><TranslatedText text="Network Status: Offline" /></span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

